"use client";

import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";

import { track } from "@vercel/analytics";
import { Check, RotateCcw, Loader2, X, Smartphone, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { reportError } from "@/lib/report-error";
import { useOrientation } from "@/components/ui/use-orientation";
import { CameraCapture, exitFullscreen } from "@/components/camera-capture";
import { ResultCard, type SearchResult } from "@/components/search-result-card";

interface DesignSearchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemName: string;
  itemIndex?: number;
  onSelect: (payload: { patentId: string; patentCdnPath: string; capturedImageDataUrl: string }) => void;
}

type FlowState = "camera" | "searching" | "results";

export function DesignSearchSheet({
  open,
  onOpenChange,
  itemName,
  itemIndex,
  onSelect,
}: DesignSearchSheetProps) {
  const [flowState, setFlowState] = useState<FlowState>("camera");
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isLandscape } = useOrientation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      exitFullscreen();
      setFlowState("camera");
      setCapturedImage(null);
      setResults([]);
      setSelectedResult(null);
      setError(null);
    }
    onOpenChange(newOpen);
  }, [onOpenChange]);

  const handleCapture = useCallback((imageDataUrl: string) => {
    setCapturedImage(imageDataUrl);
    setError(null);
  }, []);

  const performSearch = useCallback(async () => {
    if (!capturedImage) {
      setError("No image captured. Please take a photo first.");
      return;
    }

    if (!capturedImage.startsWith("data:image/")) {
      setError("Invalid image format. Please retake the photo.");
      return;
    }

    track("search_executed", { context: "hunt_item", item: itemName });
    setFlowState("searching");
    setError(null);

    try {
      const parts = capturedImage.split(",");
      if (parts.length !== 2 || !parts[1]) {
        throw new Error("Invalid image data format");
      }
      
      const [header, base64Data] = parts;
      const mimeMatch = header.match(/:(.*?);/);
      const mimeType = mimeMatch ? mimeMatch[1] : "image/jpeg";
      
      let byteString: string;
      try {
        byteString = atob(base64Data);
      } catch {
        throw new Error("Failed to decode image data");
      }
      
      if (byteString.length === 0) {
        throw new Error("Image data is empty");
      }
      
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([uint8Array], { type: mimeType });

      if (blob.size === 0) {
        throw new Error("Failed to create image blob");
      }

      if (blob.size > 20 * 1024 * 1024) {
        throw new Error("Image is too large. Please try again with a simpler scene.");
      }

      const formData = new FormData();
      formData.append("image", blob, "capture.jpg");

      const searchResponse = await fetch("/api/design-search", {
        method: "POST",
        body: formData,
      });

      const data = await searchResponse.json();

      if (!searchResponse.ok) {
        throw new Error(data.error || "Search failed");
      }

      setResults(data.results || []);
      setFlowState("results");
    } catch (err) {
      reportError(err, { component: "DesignSearchSheet", action: "performSearch" });
      setError(err instanceof Error ? err.message : "Search failed");
      setFlowState("camera");
    }
  }, [capturedImage]);

  const retakePhoto = useCallback((context: "preview" | "results") => {
    track("retake_photo", { context, item: itemName });
    setCapturedImage(null);
    setFlowState("camera");
  }, [itemName]);

  const handleConfirmSelection = useCallback(() => {
    if (selectedResult?.metadata.patent_id) {
      track("item_accepted", { item: itemName, patentId: selectedResult.metadata.patent_id });
      onSelect({
        patentId: selectedResult.metadata.patent_id,
        patentCdnPath: (selectedResult.metadata.path as string) ?? "",
        capturedImageDataUrl: capturedImage ?? "",
      });
      onOpenChange(false);
    }
  }, [selectedResult, capturedImage, onSelect, onOpenChange, itemName]);

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent
        className="!inset-0 !top-0 !mt-0 !h-dvh !max-h-dvh !w-full !rounded-none"
        showHandle={false}
        aria-describedby={undefined}
      >
        {!(isLandscape && (flowState === "camera" || flowState === "results")) && (
          <DrawerHeader className="border-b px-4 pb-3 pt-drawer-safe-top">
            <div className="flex items-center gap-2">
              <div className="flex w-10 shrink-0 items-center justify-start">
                {itemIndex != null && itemIndex > 0 && (
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground"
                    aria-hidden
                  >
                    {itemIndex}
                  </span>
                )}
              </div>
              <DrawerTitle className="min-w-0 flex-1 text-center text-xl">{itemName}</DrawerTitle>
              <div className="flex w-10 shrink-0 items-center justify-end">
                <button
                  type="button"
                  onClick={() => {
                    if (flowState === "results") track("search_results_closed", { context: "hunt_item" });
                    else if (flowState === "camera") track("camera_closed_hunt_item", { item: itemName });
                    handleOpenChange(false);
                  }}
                  className="rounded-full p-2 hover:bg-muted active:bg-muted"
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
            </div>
            {!(isLandscape && (flowState === "results" || flowState === "searching")) && (
              <DrawerDescription className="mt-2 text-sm text-muted-foreground">
                {flowState === "camera" && "Snap a photo of a real object, sketch, or drawing"}
                {(flowState === "searching" || flowState === "results") && "Select a patent that best matches this item"}
              </DrawerDescription>
            )}
          </DrawerHeader>
        )}

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {flowState === "camera" && !capturedImage && (
            <div className="relative flex min-h-0 flex-1 flex-col">
              {isLandscape && (
                <button
                  onClick={() => {
                    track("camera_closed_hunt_item", { item: itemName });
                    handleOpenChange(false);
                  }}
                  className="absolute right-safe-offset-4 top-safe-offset-4 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 active:bg-black/70"
                >
                  <X className="h-6 w-6" />
                  <span className="sr-only">Close</span>
                </button>
              )}
              <CameraCapture
                onCapture={handleCapture}
                isLandscape={isLandscape}
              />
            </div>
          )}

          {flowState === "camera" && capturedImage && (
            <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center bg-black">
              {isLandscape && (
                <button
                  onClick={() => {
                    track("camera_closed_hunt_item", { item: itemName });
                    handleOpenChange(false);
                  }}
                  className="absolute right-safe-offset-4 top-safe-offset-4 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 active:bg-black/70"
                >
                  <X className="h-6 w-6" />
                  <span className="sr-only">Close</span>
                </button>
              )}
              <img
                src={capturedImage}
                alt="Captured"
                className="h-full w-full object-cover"
              />
              {isLandscape ? (
                <div className="fixed right-safe-offset-4 top-1/2 z-10 flex -translate-y-1/2 flex-col gap-3">
                  <Button
                    size="lg"
                    variant="secondary"
                    className="h-12 w-24 gap-2 rounded-xl text-sm"
                    onClick={() => retakePhoto("preview")}
                  >
                    <RotateCcw className="size-5 shrink-0" strokeWidth={1.5} />
                    Retake
                  </Button>
                  <Button
                    size="lg"
                    className="h-12 w-24 gap-2 rounded-xl text-sm"
                    onClick={performSearch}
                  >
                    <Check className="size-5 shrink-0" strokeWidth={1.5} />
                    Search
                  </Button>
                </div>
              ) : (
                <div className="absolute inset-x-0 bottom-0 flex flex-col px-4 pb-safe-or-8">
                  {error && (
                    <div className="mb-3 flex items-center gap-2.5 rounded-xl bg-black/70 px-4 py-3 backdrop-blur-sm">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15">
                        <RotateCcw className="h-4 w-4 text-white/80" strokeWidth={2} />
                      </div>
                      <p className="min-w-0 text-sm leading-snug">
                        <span className="font-medium text-white">Search failed</span>
                        <span className="text-white/60"> — tap Search to try again</span>
                      </p>
                    </div>
                  )}
                  <div className="flex flex-row justify-center gap-4">
                    <Button
                      size="lg"
                      variant="secondary"
                      className="h-14 flex-1 gap-2 rounded-xl text-base"
                      onClick={() => retakePhoto("preview")}
                    >
                      <RotateCcw className="size-6 shrink-0" strokeWidth={1.5} />
                      Retake
                    </Button>
                    <Button
                      size="lg"
                      className="h-14 flex-1 gap-2 rounded-xl text-base"
                      onClick={performSearch}
                    >
                      <Check className="size-6 shrink-0" strokeWidth={1.5} />
                      Search
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {flowState === "searching" && (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 px-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
              <p className="text-base text-muted-foreground">Searching patents...</p>
            </div>
          )}

          {flowState === "results" && (
            <>
              {isLandscape ? (
                <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 bg-[hsl(210,40%,98%)] p-8 text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                    <Smartphone className="h-10 w-10 -rotate-90 text-primary" strokeWidth={1.5} />
                  </div>
                  <div className="space-y-3">
                    <p className="text-2xl font-bold text-foreground">Rotate to Portrait</p>
                    <p className="text-base leading-relaxed text-muted-foreground">
                      For the best experience viewing results, please rotate your device to portrait mode.
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  ref={scrollRef}
                  onScroll={(e) => setShowScrollTop(e.currentTarget.scrollTop > 300)}
                  className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain bg-[hsl(210,40%,98%)]"
                >
                  {error && (
                    <div className="px-4 py-3 text-center text-sm text-destructive">
                      {error}
                    </div>
                  )}

                  {results.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-5 p-8 text-center">
                      <p className="text-base text-muted-foreground">No matching patents found.</p>
                      <Button
                        variant="outline"
                        size="lg"
                        className="h-12 rounded-xl text-base"
                        onClick={() => retakePhoto("results")}
                      >
                        Try Another Photo
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                    {(() => {
                      const allScores = results.map(r => r.score);
                      return results.map((result, idx) => (
                        <ResultCard
                          key={result.id}
                          result={result}
                          allScores={allScores}
                          currentIndex={idx}
                          isSelected={selectedResult?.id === result.id}
                          onSelect={(r) => setSelectedResult(prev => prev?.id === r.id ? null : r)}
                          onConfirm={handleConfirmSelection}
                          onRetake={() => retakePhoto("results")}
                        />
                      ));
                    })()}
                  </div>
                )}

                  <button
                    onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
                    className={cn(
                      "sticky bottom-safe-offset-4 left-1/2 z-10 mx-auto flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-200",
                      showScrollTop ? "scale-100 opacity-100" : "pointer-events-none scale-75 opacity-0"
                    )}
                    aria-label="Scroll to top"
                  >
                    <ArrowUp className="h-5 w-5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
